// utils/salaryBaseAnalyzer.ts
// -----------------------------------------------------
// כלי עזר סטטי לחישוב סיכומי שכר בסיס לפי מעסיק/חודש/סוג תכנית,
// כולל חלוקה לפי סוג מוצר (קרן השתלמות לעומת "אחר")
// ושבירה לפי סוג רכיב ההפקדה (byContributionType).
// דרישות עדכניות:
// 1) GroupBy נוסף לפי policy.plan_or_account_type
// 2) לא ליצור קבוצות שסך הכל שלהן 0 *אלא אם* יש הפקדות > 0 (גם אם salaryBase=0)
// 3) בחירת "החודש האחרון" לכל מעסיק ולכל planType
// 4) אם salary_month חסר – משתמשים ב-payment_period כ-period key
//
// Salary base resolution rules (applied per policyId+period in applySummaryForPeriod):
// R1) Only positive deposits (paid >= 0) contribute to salary base.
// R2) Active managers insurance policies whose only positive deposits are disability insurance
//     are excluded from salary base entirely.
// R3) If reported salary_base is 0 → derive base from paid / (rate / 100).
// R4) If reported salary_base > 0: for each contribution type (CT) with paid > 0,
//     compute actualRate = paid / reported. If |actualRate - declaredRate| >= 0.2%,
//     that CT is considered "deviating".
// R5 (NEW): If fewer than 2 CTs deviate → keep reported base (rate mismatch is likely
//     a reporting error on the rate, not the base).
// R6) If >= 2 CTs deviate → override with derived base = max of (paid / rate) across
//     deviating CTs. mergedEffectiveSalaryBase is set for strikethrough display.
// R7) Per-policyId: take the max base across all positive deposits for that policy.
// R8) Dedup for managers insurance / provident fund policies sharing an employer+period+planType:
//     if all deduped policies have the same base → count once; otherwise sum them.
// -----------------------------------------------------

import { Contact } from "../../interfaces/DTO/crm/contact";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { ProductType, AdjustedProductType, ContributionType } from "../../interfaces/DTO/mislaka_xml_models";
import { MimshakDataUtil } from "../mimshakDataUtil";


export interface SalaryByMonth {
  salaryBase: number;
  reportedSalaryBase?: number; // הערך המקורי המדווח, כשהוחלף ע"י בסיס שכר מחושב
  mergedEffectiveSalaryBase?: number; // מקסימום בסיס שכר אפקטיבי לפי rate לאחר merge
  byContributionType: Record<number, number>;
  _effectiveByCt?: Record<number, number>; // internal: effective sum per contribution type
  _reportedByCt?: Record<number, number>;  // internal: reported sum per contribution type
  planOrAccountType?: number; // לצורך UI (Chip)
   hasMissingSalaryBase?: boolean;
   hasPaymentDelay?: boolean;
   hasPartialLatestSalary?: boolean;
}

// employer -> periodKey -> planType -> data
// periodKey: "YYYY-MM" או "pp:<payment_period>"
export type EmployerMonthMap = Record<string, Record<string, Record<number, SalaryByMonth>>>;

// מבנה חדש שמחזיק רק את התאריך האחרון לכל מעסיק ו-planType
export type EmployerMonthMapByPlanType = Record<string, Record<number, { period: string; data: SalaryByMonth }>>;

// מבנה שמחזיק מערך של מעסיקים לכל planType - כל המעסיקים מהתקופה האחרונה + ישנים יותר בהפרש של חודש אחד בלבד
export type LatestByPlanType = Record<number, Array<{ period: string; employer: string; data: SalaryByMonth }>>;

export interface ContactSalarySummary {
  hishtalmut: LatestByPlanType; // קרנות השתלמות - כל מעסיקים מהתקופה האחרונה + מעסיקים בהפרש חודש אחד
  other: LatestByPlanType; // מוצרים אחרים - כל מעסיקים מהתקופה האחרונה + מעסיקים בהפרש חודש אחד
  hishtalmutFull?: EmployerMonthMap; // כל התקופות - לצורך תצוגה מורחבת
  otherFull?: EmployerMonthMap; // כל התקופות - לצורך תצוגה מורחבת
}
// Raw deposit data per CT, collected before the salary base resolution decision is made.
// Stored as arrays so all deposits for a policyId+CT are available when applying rules R3–R7.
type CtDepositEntry = { reported: number; paid: number; rate: number | null | undefined };
// planType -> policyId -> ctKey -> deposits[]
type PolicyCtMap = Record<number, Record<string, Record<number, CtDepositEntry[]>>>;

type TempBase = Record<
  string, // employer
  Record<
    string, // periodKey (YYYYMM)
    {
      hishtalmut: PolicyCtMap;
      other: PolicyCtMap;
    }
  >
>;

// Tracks which policyIds require salary base dedup (managers insurance or provident fund),
// keyed by "employer-periodKey-planType"
type DeduplicatedSalaryBaseSet = Record<string, Set<string>>;

type DelayMaps = {
  paymentPeriodToSalaryMonths: Record<string, Set<string>>; // key: employer-planType-pp -> Set(periodKey)
  periodKeyToPaymentPeriods: Record<string, Set<number>>;    // key: employer-planType-periodKey -> Set(pp)
};

export type SalaryBaseSummary = Record<string, ContactSalarySummary>; // contactId -> summary

/**
 * מחלקה סטטית האחראית לחישוב כל הסיכומים מתוך MimshakData.
 */
export class SalaryBaseAnalyzer {

  /** מנרמל שם מעסיק לצורך השוואה: מסיר תווי פיסוק ומרווחים עודפים */
  static normalizeEmployerName(name: string): string {
    return name
      .replace(/["""״'''.,()\-]/g, "")  // פיסוק נפוץ
      .replace(/\s+/g, " ")             // רווחים מרובים → רווח אחד
      .trim()
      .toLowerCase();
  }

/** מחזיר periodKey מתאים: תמיד בפורמט "YYYYMM" */
  static buildPeriodKey(salaryMonth?: string | null, paymentPeriod?: number | null): string {
  if (salaryMonth && /^\d{6}$/.test(salaryMonth)) {
    return salaryMonth; // למשל "202502"
  }
  if (paymentPeriod !== undefined && paymentPeriod !== null) {
    return paymentPeriod.toString(); // גם הוא "202503"
  }
  return "000000"; // ברירת מחדל שמרנית
}

 /** ממיר periodKey לערך מספרי לצורך מיון יורד */
static toSortValue(periodKey: string): number {
  return Number(periodKey) || -Infinity; // "202502" -> 202502
}

  /** השוואה יורדת בין periodKeys */
  static comparePeriodKeysDesc(a: string, b: string): number {
    return SalaryBaseAnalyzer.toSortValue(b) - SalaryBaseAnalyzer.toSortValue(a);
  }

  /**
   * הפונקציה המרכזית שמייצרת מפת סיכומים לכל איש קשר.
   * GroupBy: productType -> employer -> periodKey -> plan_or_account_type
   * מחזירה רק את התאריך האחרון לכל מעסיק ו-planType
   */
static getSummary(
  contacts: Contact[],
  mimshakData: MimshakData[]
): SalaryBaseSummary {
  const summary: SalaryBaseSummary = {};

  contacts.forEach((contact) => {
    const products = MimshakDataUtil.getProductsByContactId(mimshakData, contact.id);

    const hishtalmutFull: EmployerMonthMap = {};
    const otherFull: EmployerMonthMap = {};
    const tempBase: TempBase = {};
    const delayMaps: DelayMaps = {
      paymentPeriodToSalaryMonths: {},
      periodKeyToPaymentPeriods: {},
    };
    const dedupSalaryBaseSet: DeduplicatedSalaryBaseSet = {};
    const employerDisplayNames: Record<string, string> = {}; // normalized key -> שם מקורי ראשון

    // 1) איסוף נתונים גולמיים (+מיפויי עיכובים) לתוך tempBase וה־maps
    SalaryBaseAnalyzer.collectRaw(products, tempBase, delayMaps, hishtalmutFull, otherFull, dedupSalaryBaseSet, employerDisplayNames);

    // 2) סיכום בסיס + דגלים: חסר בסיס / עיכוב תשלום
    Object.entries(tempBase).forEach(([employer, periods]) => {
      Object.entries(periods).forEach(([periodKey, categories]) => {
        SalaryBaseAnalyzer.applySummaryForPeriod(
          categories.hishtalmut,
          hishtalmutFull,
          employer,
          periodKey,
          delayMaps,
          dedupSalaryBaseSet
        );
        SalaryBaseAnalyzer.applySummaryForPeriod(
          categories.other,
          otherFull,
          employer,
          periodKey,
          delayMaps,
          dedupSalaryBaseSet
        );
      });
    });

    // 3) סימון "חודש שכר אחרון חלקי" – אך ורק לחודש האחרון לכל employer+planType
    SalaryBaseAnalyzer.markPartialLatest(hishtalmutFull, delayMaps);
    SalaryBaseAnalyzer.markPartialLatest(otherFull, delayMaps);

    // 4) ניקוי קבוצות ריקות
    SalaryBaseAnalyzer.pruneEmpty(hishtalmutFull);
    SalaryBaseAnalyzer.pruneEmpty(otherFull);

    // 5) שחזור שמות תצוגה (מחליף normalized keys בשמות המקוריים)
    SalaryBaseAnalyzer.restoreDisplayNames(hishtalmutFull, employerDisplayNames);
    SalaryBaseAnalyzer.restoreDisplayNames(otherFull, employerDisplayNames);

    // 6) המרה למבנה חדש - רק התאריך האחרון לכל מעסיק ו-planType
    const hishtalmut = SalaryBaseAnalyzer.convertToLatestPeriodMap(hishtalmutFull, false);
    const other = SalaryBaseAnalyzer.convertToLatestPeriodMap(otherFull, true); // isPension=true

    summary[contact.id] = {
      hishtalmut,
      other,
      hishtalmutFull,
      otherFull
    };
  });

  return summary;
}
// --- עזרי עיכוב ---
private static delayInMonths(
  paymentPeriod: number | null | undefined,
  salaryMonth?: string | null
): number {
  const sm = salaryMonth && /^\d{6}$/.test(salaryMonth) ? Number(salaryMonth) : null;
  const pp = typeof paymentPeriod === "number" ? paymentPeriod : null;
  if (!sm || !pp) return 0;
  const smY = Math.floor(sm / 100), smM = sm % 100;
  const ppY = Math.floor(pp / 100), ppM = pp % 100;
  return (ppY - smY) * 12 + (ppM - smM);
}

// --- לוודא שקבוצה קיימת ולהחזיר רפרנס ---
private static ensureGroupRef(
  target: EmployerMonthMap,
  employer: string,
  periodKey: string,
  planType: number
): SalaryByMonth {
  if (!target[employer]) target[employer] = {};
  if (!target[employer][periodKey]) target[employer][periodKey] = {};
  if (!target[employer][periodKey][planType]) {
    target[employer][periodKey][planType] = {
      salaryBase: 0,
      byContributionType: {},
      planOrAccountType: planType,
    };
  }
  return target[employer][periodKey][planType];
}

// --- רישום מפות עיכוב ---
private static recordDelayMaps(
  employer: string,
  planType: number,
  periodKey: string,
  paymentPeriod: number | null | undefined,
  delayMaps: DelayMaps
) {
  const { paymentPeriodToSalaryMonths, periodKeyToPaymentPeriods } = delayMaps;

  // לכל payment_period – באילו periodKeys (חודשי שכר) הוא הופיע
  const payKey = `${employer}-${planType}-${paymentPeriod}`;
  if (!paymentPeriodToSalaryMonths[payKey]) {
    paymentPeriodToSalaryMonths[payKey] = new Set<string>();
  }
  if (paymentPeriod != null) {
    paymentPeriodToSalaryMonths[payKey].add(periodKey);
  }

  // לכל periodKey – אילו payment_periods הופיעו בו
  const p2kKey = `${employer}-${planType}-${periodKey}`;
  if (!periodKeyToPaymentPeriods[p2kKey]) {
    periodKeyToPaymentPeriods[p2kKey] = new Set<number>();
  }
  if (typeof paymentPeriod === "number") {
    periodKeyToPaymentPeriods[p2kKey].add(paymentPeriod);
  }
}

// --- איסוף נתונים גולמיים ל-tempBase ולמפות העיכוב, ובניית פירוט רכיבי הפקדה ---
private static collectRaw(
  products: ReturnType<typeof MimshakDataUtil.getProductsByContactId>,
  tempBase: TempBase,
  delayMaps: DelayMaps,
  hishtalmut: EmployerMonthMap,
  other: EmployerMonthMap,
  dedupSalaryBaseSet: DeduplicatedSalaryBaseSet,
  employerDisplayNames: Record<string, string>
) {
  // policyIds של ביטוח מנהלים פעיל שכל הפקדותיהם הן אובדן כושר עבודה בלבד —
  // בסיס השכר שלהם לא נספר בסכום השכר לפנסיה
  const managersInsuranceDisabilityOnlyPolicies = new Set<string>();

  products.forEach((product, productIndex) => {
    const adjustedType = product.details?.adjusted_product_type;
    if (adjustedType !== AdjustedProductType.ACTIVE_MANAGERS_INSURANCE) return;

    product.policies_or_accounts.forEach((pa, policyIndex) => {
      const policyId = `${product.details?.product_type}-${productIndex}-${policyIndex}`;
      const nonDisabilityDeposit = (pa.deposits || []).some(
        (d) => (d.deposit_amount_paid || 0) > 0 && d.contribution_type !== ContributionType.DISABILITY_INSURANCE
      );
      if (!nonDisabilityDeposit) {
        managersInsuranceDisabilityOnlyPolicies.add(policyId);
      }
    });
  });

  products.forEach((product, productIndex) => {
    const productType = product.details?.product_type;
    const adjustedType = product.details?.adjusted_product_type;

    // adjustedType הוא האמת; productType הוא fallback לגרסאות ישנות
    const isHishtalmutByAdjusted = (at: number) => at === AdjustedProductType.EDUCATION_FUND;
    const isOtherByAdjusted = (at: number) =>
      at === AdjustedProductType.LIFE_INSURANCE_SAVINGS ||
      at === AdjustedProductType.PENSION_FUND ||
      at === AdjustedProductType.PROVIDENT_FUND ||
      at === AdjustedProductType.PURE_SAVINGS_POLICY ||
      at === AdjustedProductType.ACTIVE_MANAGERS_INSURANCE ||
      at === AdjustedProductType.INACTIVE_MANAGERS_INSURANCE ||
      at === AdjustedProductType.MANDATORY_PENSION_FUND ||
      at === AdjustedProductType.SUPPLEMENTARY_PENSION_FUND ||
      at === AdjustedProductType.BASIC_PENSION_FUND ||
      at === AdjustedProductType.OLD_COMPREHENSIVE_PENSION ||
      at === AdjustedProductType.OLD_BASIC_PENSION ||
      at === AdjustedProductType.CAPITAL_PROVIDENT_FUND ||
      at === AdjustedProductType.PENSION_PROVIDENT_FUND ||
      at === AdjustedProductType.COMBINED_PROVIDENT_FUND;

    const isHishtalmutByProductType = (pt: number) => pt === ProductType.EDUCATION_FUND;
    const isOtherByProductType = (pt: number) =>
      pt === ProductType.PENSION_FUND ||
      pt === ProductType.PROVIDENT_FUND ||
      pt === ProductType.LIFE_INSURANCE_SAVINGS ||
      pt === ProductType.LIFE_INSURANCE_AND_PURE_SAVINGS;

    let category: "hishtalmut" | "other" | null = null;
    if (adjustedType != null) {
      if (isHishtalmutByAdjusted(adjustedType)) category = "hishtalmut";
      else if (isOtherByAdjusted(adjustedType)) category = "other";
      else category = null;
    } else if (productType != null) {
      if (isHishtalmutByProductType(productType)) category = "hishtalmut";
      else if (isOtherByProductType(productType)) category = "other";
      else category = null;
    }

    if (!category) return;

    product.policies_or_accounts.forEach((pa, policyIndex) => {
      const policyId = `${productType}-${productIndex}-${policyIndex}`;
      const planType = pa.plan_or_account_type ?? 0;
      const excludeFromSalaryBase = managersInsuranceDisabilityOnlyPolicies.has(policyId);
      const adjustedType = product.details?.adjusted_product_type;


      (pa.deposits || []).forEach((deposit) => {
        const periodKey = SalaryBaseAnalyzer.buildPeriodKey(
          deposit.salary_month,
          deposit.payment_period
        );
        const rawEmployer = deposit.employer_name || "מעסיק לא ידוע";
        const employer = SalaryBaseAnalyzer.normalizeEmployerName(rawEmployer);
        if (!employerDisplayNames[employer]) employerDisplayNames[employer] = rawEmployer;

        // tempBase init
        if (!tempBase[employer]) tempBase[employer] = {};
        if (!tempBase[employer][periodKey]) {
          tempBase[employer][periodKey] = { hishtalmut: {}, other: {} };
        }
        if (!tempBase[employer][periodKey][category][planType]) {
          tempBase[employer][periodKey][category][planType] = {};
        }

        // Collect raw per-CT deposit data for salary base resolution (rules R3–R7 applied later).
        // Only positive deposits contribute to salary base (R1), excluded policies skipped (R2).
        const reported = deposit.salary_base || 0;
        const rate = deposit.deposit_rate;
        const paid = deposit.deposit_amount_paid || 0;
        const ct = deposit.contribution_type as number;

        if (paid >= 0 && !excludeFromSalaryBase) {
          const policyCtMap = tempBase[employer][periodKey][category][planType];
          if (!policyCtMap[policyId]) policyCtMap[policyId] = {};
          if (!policyCtMap[policyId][ct]) policyCtMap[policyId][ct] = [];
          policyCtMap[policyId][ct].push({ reported, paid, rate });
        }

        // סימון policyIds של ביטוח מנהלים וקופת גמל לצורך dedup בשלב הסיכום
        if (
          adjustedType === AdjustedProductType.ACTIVE_MANAGERS_INSURANCE ||
          adjustedType === AdjustedProductType.INACTIVE_MANAGERS_INSURANCE ||
          productType === ProductType.PROVIDENT_FUND
        ) {
          const miKey = `${employer}-${periodKey}-${planType}`;
          if (!dedupSalaryBaseSet[miKey]) dedupSalaryBaseSet[miKey] = new Set<string>();
          dedupSalaryBaseSet[miKey].add(policyId);
        }

        // בניית פירוט רכיבי הפקדה
        const group = category === "hishtalmut" ? hishtalmut : other;
        const ref = SalaryBaseAnalyzer.ensureGroupRef(group, employer, periodKey, planType);
        ref.byContributionType[ct] = (ref.byContributionType[ct] || 0) + paid;



        // רישום מפות עיכוב
        SalaryBaseAnalyzer.recordDelayMaps(
          employer,
          planType,
          periodKey,
          deposit.payment_period,
          delayMaps
        );
      });
    });
  });
}

// --- סיכום לקבוצת תקופה אחת (מחשב salaryBase + דגלים: חסר בסיס / עיכוב) ---
private static applySummaryForPeriod(
  categories: PolicyCtMap, // planType -> policyId -> ct -> deposits[]
  target: EmployerMonthMap,
  employer: string,
  periodKey: string,
  delayMaps: DelayMaps,
  dedupSalaryBaseSet: DeduplicatedSalaryBaseSet
) {
  const { paymentPeriodToSalaryMonths, periodKeyToPaymentPeriods } = delayMaps;

  Object.keys(categories).forEach((planKey) => {
    const planType = Number(planKey);
    const policyCtMap = categories[planType];
    if (!policyCtMap) return;

    const miKey = `${employer}-${periodKey}-${planType}`;
    const miPolicyIds = dedupSalaryBaseSet[miKey];

    // Resolve salary base per policyId applying rules R3–R7.
    // Returns { resolvedBase, reportedBase, usedDerived }.
    const resolvePolicy = (policyId: string): { resolvedBase: number; reportedBase: number; usedDerived: boolean } => {
      const ctMap = policyCtMap[policyId];
      if (!ctMap) return { resolvedBase: 0, reportedBase: 0, usedDerived: false };

      // Collect all positive deposits grouped by CT
      const ctEntries = Object.entries(ctMap).map(([ctKey, deposits]) => {
        const posDeposits = deposits.filter((d) => d.paid >= 0);
        const totalPaid = posDeposits.reduce((s, d) => s + d.paid, 0);
        // Max reported across positive deposits for this CT
        const maxReported = posDeposits.reduce((s, d) => Math.max(s, d.reported), 0);
        // Use last deposit's rate (they share the same policy, rates should be consistent)
        const rate = posDeposits.length > 0 ? posDeposits[posDeposits.length - 1].rate : null;
        return { ct: Number(ctKey), totalPaid, maxReported, rate };
      }).filter((e) => e.totalPaid > 0);

      if (ctEntries.length === 0) return { resolvedBase: 0, reportedBase: 0, usedDerived: false };

      // R3: if no reported base anywhere → derive from any CT
      const maxReportedAcrossCts = ctEntries.reduce((s, e) => Math.max(s, e.maxReported), 0);
      if (maxReportedAcrossCts <= 0) {
        // derive from first CT that has rate
        const withRate = ctEntries.find((e) => e.rate != null && e.rate > 0);
        if (!withRate) return { resolvedBase: 0, reportedBase: 0, usedDerived: false };
        return { resolvedBase: withRate.totalPaid / (withRate.rate! / 100), reportedBase: 0, usedDerived: true };
      }

      // R4: count deviating CTs
      const deviatingCts = ctEntries.filter((e) => {
        if (e.maxReported <= 0 || e.rate == null || e.rate <= 0 || e.totalPaid <= 0) return false;
        const actualRate = Math.round((e.totalPaid / e.maxReported) * 1000) / 10;
        const declaredRate = Math.round(e.rate * 10) / 10;
        return Math.abs(actualRate - declaredRate) >= 0.2;
      });

      // R5: fewer than 2 deviating CTs → keep reported base (rate mismatch, not base mismatch)
      if (deviatingCts.length < 2) {
        const reportedBase = ctEntries.reduce((s, e) => Math.max(s, e.maxReported), 0);
        return { resolvedBase: reportedBase, reportedBase, usedDerived: false };
      }

      // R6: >= 2 deviating CTs → use derived base = max of (paid / rate) across deviating CTs
      const derivedBase = deviatingCts.reduce((s, e) => {
        const derived = e.totalPaid / (e.rate! / 100);
        return Math.max(s, derived);
      }, 0);
      const reportedBase = ctEntries.reduce((s, e) => Math.max(s, e.maxReported), 0);
      return { resolvedBase: derivedBase, reportedBase, usedDerived: true };
    };

    // Resolve all policies and build policyId -> resolvedBase / reportedBase maps
    const resolvedMap: Record<string, number> = {};
    const reportedMap: Record<string, number> = {};
    const derivedFlags: Record<string, boolean> = {};
    Object.keys(policyCtMap).forEach((policyId) => {
      const { resolvedBase, reportedBase, usedDerived } = resolvePolicy(policyId);
      resolvedMap[policyId] = resolvedBase;
      reportedMap[policyId] = reportedBase;
      derivedFlags[policyId] = usedDerived;
    });

    // R8: dedup for managers insurance / provident fund (calcSum unchanged)
    const calcSum = (source: Record<string, number>) => {
      if (miPolicyIds && miPolicyIds.size > 0) {
        const miValues = Array.from(miPolicyIds).map((id) => Math.round(source[id] ?? 0));
        const allSame = miValues.every((v) => v === miValues[0]);
        const nonMiValues = Object.entries(source)
          .filter(([id]) => !miPolicyIds.has(id))
          .map(([, v]) => v);
        const miContribution = allSame ? miValues[0] : miValues.reduce((a, b) => a + b, 0);
        return miContribution + nonMiValues.reduce((a, b) => a + b, 0);
      }
      return Object.values(source).reduce((a, b) => a + b, 0);
    };

    const sumBase = calcSum(resolvedMap);
    const sumReported = calcSum(reportedMap);
    const anyDerived = Object.values(derivedFlags).some(Boolean);

    const hasBase = Object.values(resolvedMap).some((v) => (v || 0) > 0);
    const hasMissingBase = Object.values(resolvedMap).some((v) => (v || 0) <= 0);

    // האם יש הפקדות בפועל?
    const contribRef = target[employer]?.[periodKey]?.[planType];
    const hasContrib =
      !!contribRef &&
      Object.values(contribRef.byContributionType || {}).some((v) => v > 0);

    // חישוב עיכובים (> חודש)
    const p2kKey = `${employer}-${planType}-${periodKey}`;
    const pps = Array.from(periodKeyToPaymentPeriods[p2kKey] || []);
    const delays: number[] = [];
    pps.forEach((pp) => {
      const payKey = `${employer}-${planType}-${pp}`;
      const smKeys = Array.from(paymentPeriodToSalaryMonths[payKey] || []);
      smKeys.forEach((smKey) => {
        delays.push(SalaryBaseAnalyzer.delayInMonths(pp, smKey));
      });
    });
    const hasSevereDelay = delays.some((d) => d > 1);

    if (hasBase || hasContrib) {
      const ref = SalaryBaseAnalyzer.ensureGroupRef(target, employer, periodKey, planType);

      // When derived base was used (R6): salaryBase = reported (shown with strikethrough),
      // mergedEffectiveSalaryBase = derived (the actual value used for % calculations).
      if (anyDerived && sumReported > 0 && Math.round(sumReported) !== Math.round(sumBase)) {
        ref.salaryBase = sumReported;
        ref.reportedSalaryBase = sumReported;
        ref.mergedEffectiveSalaryBase = sumBase;
      } else {
        ref.salaryBase = sumBase;
      }

      // חסר בסיס: רק אם יש גם הפקדות
      if ((!hasBase && hasContrib) || (hasMissingBase && hasContrib)) {
        ref.hasMissingSalaryBase = true;
      }

      if (hasSevereDelay) {
        ref.hasPaymentDelay = true;
      }
    }
  });
}

// --- סימון "חודש אחרון חלקי" (רק לחודש האחרון לכל employer+planType) ---
private static markPartialLatest(emap: EmployerMonthMap, delayMaps: DelayMaps) {
  const { paymentPeriodToSalaryMonths, periodKeyToPaymentPeriods } = delayMaps;

  Object.entries(emap).forEach(([employer, periods]) => {
    // כל ה-planTypes אצל אותו מעסיק
    const planTypes = new Set<number>();
    Object.values(periods).forEach((plans) => {
      Object.keys(plans).forEach((pk) => planTypes.add(Number(pk)));
    });

    planTypes.forEach((planType) => {
      // כל ה-periodKeys עבור planType
      const periodKeys = Object.keys(periods)
        .filter((pk) => periods[pk] && periods[pk][planType])
        .sort(SalaryBaseAnalyzer.comparePeriodKeysDesc);

      if (periodKeys.length === 0) return;

      const latestKey = periodKeys[0];

      // אילו payment_periods הופיעו בתוך ה-latestKey?
      const p2kKey = `${employer}-${planType}-${latestKey}`;
      const paymentPeriodsInLatest = Array.from(periodKeyToPaymentPeriods[p2kKey] || []);

      // חלקי: יש payment_period שמשויך גם ל-periodKey עתידי מעבר ל-latestKey
      // (כלומר: חודש תשלום הזה שייך גם לחודש שכר עתידי שטרם נקלט במלואו)
      const isPartial = paymentPeriodsInLatest.some((pp) => {
        const payKey = `${employer}-${planType}-${pp}`;
        const relatedSalaryKeys = Array.from(paymentPeriodToSalaryMonths[payKey] || []);
        return relatedSalaryKeys.some((k) => k > latestKey);
      });


      if (isPartial && periods[latestKey]?.[planType]) {
        periods[latestKey][planType].hasPartialLatestSalary = true;
      }
    });
  });
}

// --- ניקוי קבוצות ללא בסיס וללא הפקדות ---
private static pruneEmpty(emap: EmployerMonthMap) {
  Object.entries(emap).forEach(([employer, periods]) => {
    Object.entries(periods).forEach(([periodKey, plans]) => {
      Object.entries(plans).forEach(([planKey, val]) => {
        const hasBase = (val.salaryBase || 0) > 0;
        const hasContrib = Object.values(val.byContributionType || {}).some((v) => v > 0);
        if (!hasBase && !hasContrib) {
          delete emap[employer][periodKey][Number(planKey)];
        }
      });
      if (Object.keys(emap[employer][periodKey]).length === 0) {
        delete emap[employer][periodKey];
      }
    });
    if (Object.keys(emap[employer]).length === 0) {
      delete emap[employer];
    }
  });
}

// --- החלפת normalized employer keys בשמות תצוגה מקוריים ---
private static restoreDisplayNames(
  emap: EmployerMonthMap,
  displayNames: Record<string, string>
) {
  Object.keys(emap).forEach((normalizedKey) => {
    const displayName = displayNames[normalizedKey];
    if (displayName && displayName !== normalizedKey) {
      emap[displayName] = emap[normalizedKey];
      delete emap[normalizedKey];
    }
  });
}

// --- פונקצית עזר לחישוב הפרש חודשים בין שני periodKeys ---
private static monthDifference(period1: string, period2: string): number {
  const p1 = Number(period1);
  const p2 = Number(period2);

  const y1 = Math.floor(p1 / 100);
  const m1 = p1 % 100;
  const y2 = Math.floor(p2 / 100);
  const m2 = p2 % 100;

  return Math.abs((y1 - y2) * 12 + (m1 - m2));
}

// --- המרה למבנה שמחזיק מערך של מעסיקים לכל planType ---
// כלל: כל המעסיקים מהתקופה האחרונה + מעסיקים ישנים יותר בהפרש של חודש אחד בלבד
// עבור פנסיה: אם החודש האחרון עבור מעסיק מסוים עם דיווח חלקי, בוחרים את החודש הקודם אם הוא עם דיווח מלא
private static convertToLatestPeriodMap(emap: EmployerMonthMap, isPension: boolean = false): LatestByPlanType {
  const result: LatestByPlanType = {};

  // אוספים את כל ה-planTypes שקיימים
  const planTypes = new Set<number>();
  Object.values(emap).forEach((periods) => {
    Object.values(periods).forEach((plans) => {
      Object.keys(plans).forEach((pk) => planTypes.add(Number(pk)));
    });
  });

  // לכל planType, מוצאים את כל המעסיקים שצריכים להיות מוצגים
  planTypes.forEach((planType) => {
    const allCandidates: Array<{
      employer: string;
      periodKey: string;
      values: SalaryByMonth;
    }> = [];

    // אוספים את כל המועמדים מכל המעסיקים, ובוחרים את התקופה הנכונה לכל מעסיק
    Object.entries(emap).forEach(([employer, periods]) => {
      // מוצאים את כל התקופות של המעסיק הזה עבור ה-planType
      const employerPeriods: Array<{ periodKey: string; values: SalaryByMonth }> = [];
      Object.entries(periods).forEach(([periodKey, plans]) => {
        if (plans[planType]) {
          employerPeriods.push({ periodKey, values: plans[planType] });
        }
      });

      if (employerPeriods.length === 0) return;

      // ממיינים מהחדש לישן
      employerPeriods.sort((a, b) => SalaryBaseAnalyzer.comparePeriodKeysDesc(a.periodKey, b.periodKey));

      // בוחרים את התקופה המתאימה
      let selectedPeriod = employerPeriods[0];

      // עבור פנסיה: אם החודש האחרון עם דיווח חלקי, נבדוק את החודש הקודם
      if (isPension && employerPeriods[0].values.hasPartialLatestSalary && employerPeriods.length >= 2) {
        // נבדוק אם החודש השני (הקודם) הוא עם דיווח מלא
        if (!employerPeriods[1].values.hasPartialLatestSalary) {
          selectedPeriod = employerPeriods[1];
        }
      }


      allCandidates.push({
        employer,
        periodKey: selectedPeriod.periodKey,
        values: selectedPeriod.values,
      });
    });

    if (allCandidates.length === 0) return;

    // ממיינים מהחדש לישן
    allCandidates.sort((a, b) => SalaryBaseAnalyzer.comparePeriodKeysDesc(a.periodKey, b.periodKey));

    // מציאת התקופה האחרונה ביותר
    const latestPeriod = allCandidates[0].periodKey;

    // סינון: כל המעסיקים מהתקופה האחרונה + מעסיקים בהפרש של חודש אחד בלבד
    const selectedCandidates = allCandidates.filter((candidate) => {
      const diff = SalaryBaseAnalyzer.monthDifference(latestPeriod, candidate.periodKey);
      return diff === 0 || diff === 1;
    });


    // המרה למבנה החדש - מערך של מעסיקים
    result[planType] = selectedCandidates.map((candidate) => ({
      period: candidate.periodKey,
      employer: candidate.employer,
      data: candidate.values,
    }));
  });

  return result;
}



  /**
   * מחזיר לכל מעסיק את ה-period האחרון *לכל planType* במבנה הנתון.
   */
static pickLatestMonthPerEmployer(group: EmployerMonthMap): Array<{
  employer: string;
  month: string; // YYYYMM
  planOrAccountType: number;
  values: SalaryByMonth;
}> {
  const out: Array<{ employer: string; month: string; planOrAccountType: number; values: SalaryByMonth }> = [];

  Object.entries(group).forEach(([employer, periods]) => {
    // אספי את כל המפתחות של תקופות השכר ממוינים מהחדש לישן
    const allPeriodsDesc = Object.keys(periods).sort(SalaryBaseAnalyzer.comparePeriodKeysDesc);

    // נבנה מועמדים לכל planType לפי סדר יורד, רק אם יש בסיס > 0 או יש הפקדות
    const candidatesPerPlan: Record<number, Array<{ month: string; values: SalaryByMonth }>> = {};

    allPeriodsDesc.forEach((periodKey) => {
      const plans = periods[periodKey];
      Object.entries(plans).forEach(([planKey, values]) => {
        const planType = Number(planKey);
        const hasBase = (values.salaryBase || 0) > 0;
        const hasContrib = Object.values(values.byContributionType || {}).some((v) => v > 0);
        if (!(hasBase || hasContrib)) return;
        if (!candidatesPerPlan[planType]) candidatesPerPlan[planType] = [];
        candidatesPerPlan[planType].push({ month: periodKey, values });
      });
    });

    // בחירה לכל planType לפי הכלל:
    // 1) ברירת מחדל: המועמד הראשון (הכי חדש)
    // 2) אם הראשון hasPartialLatestSalary=true ויש שני: אם השני לא partial -> בוחרים אותו; אחרת נשארים עם הראשון
    Object.entries(candidatesPerPlan).forEach(([planKey, candidates]) => {
      if (!candidates || candidates.length === 0) return;

      let pick = candidates[0];
      const firstIsPartial = !!pick.values.hasPartialLatestSalary;

      if (firstIsPartial && candidates.length >= 2) {
        const second = candidates[1];
        const secondIsPartial = !!second.values.hasPartialLatestSalary;
        if (!secondIsPartial) {
          pick = second;
        }
        // אם גם השני partial – נשארים עם הראשון (כפי שביקשת)
      }

      out.push({
        employer,
        month: pick.month,
        planOrAccountType: Number(planKey),
        values: pick.values,
      });
    });
  });

  return out;
}


  /** מחשב את סך האחוזים מכל רכיבי ההפקדה מתוך שכר הבסיס (לטובת תצוגה). */
  static calcTotalPercent(values: SalaryByMonth): number {
    const { salaryBase, byContributionType } = values;
    if (!salaryBase || salaryBase <= 0) return 0;
    const total = Object.values(byContributionType).reduce((acc, amt) => acc + (amt / salaryBase) * 100, 0);
    return total;
  }
}

// -----------------------------------------------------
// דוגמת שימוש (בקומפוננטה או hook):
// const summary = useMemo(() => SalaryBaseAnalyzer.getSummary(contacts, mimshakData), [contacts, mimshakData]);
// const latest = SalaryBaseAnalyzer.pickLatestMonthPerEmployer(summary[contactId].other);
// הערה: שדה month עשוי להיות "YYYY-MM" או "pp:<n>"
// -----------------------------------------------------
